import { useQuery } from "@tanstack/react-query";
import {
	ArrowDownLeft,
	ArrowLeftRight,
	ArrowRight,
	ArrowUpRight,
	Banknote,
	CircleAlert,
	Landmark,
	LoaderCircle,
} from "lucide-react";
import { useEffect, useState } from "react";

import { api, type TreasuryList } from "@/client";
import { MetricCard } from "@/components/MetricCard";
import { FinancePresentation } from "@/presentation/finance";

type TreasuryAccount = TreasuryList["accounts"][number];

export function TreasuryView() {
	const query = useQuery({
		queryKey: ["treasury", "current-month"],
		queryFn: () => api.treasury.list(),
	});
	const [selectedAccountId, setSelectedAccountId] = useState<string>();

	useEffect(() => {
		if (query.data && !query.data.accounts.some((account) => account.id === selectedAccountId)) {
			setSelectedAccountId(query.data.accounts[0]?.id);
		}
	}, [query.data, selectedAccountId]);

	if (query.isPending) {
		return (
			<div className="grid min-h-80 place-items-center rounded-3xl border border-stone-200 bg-white text-stone-600 shadow-sm">
				<div className="flex items-center gap-3 text-sm font-medium">
					<LoaderCircle className="size-5 animate-spin" />
					Montando o livro de tesouraria…
				</div>
			</div>
		);
	}

	if (query.error) {
		return (
			<div className="rounded-3xl border border-red-200 bg-white p-8 text-center shadow-sm">
				<CircleAlert className="mx-auto mb-3 size-8 text-red-600" />
				<h2 className="font-semibold text-stone-950">Não foi possível abrir as contas</h2>
				<p className="mt-2 text-sm text-stone-600">{query.error.message}</p>
			</div>
		);
	}

	const data = query.data;
	const selectedAccount =
		data.accounts.find((account) => account.id === selectedAccountId) ?? data.accounts[0];
	const itau = data.accounts.find((account) => account.id === "itau-58000");
	const cashAmanda = data.accounts.find((account) => account.id === "caixa-amanda");
	const cashSindi = data.accounts.find((account) => account.id === "caixa-sindi");

	if (!itau || !cashAmanda || !cashSindi) {
		return (
			<div className="rounded-3xl border border-red-200 bg-white p-8 text-center shadow-sm">
				<CircleAlert className="mx-auto mb-3 size-8 text-red-600" />
				<h2 className="font-semibold text-stone-950">O cadastro das contas está incompleto</h2>
				<p className="mt-2 text-sm text-stone-600">
					A conta Itaú 58000, o Caixa Amanda e o Caixa Sindi são necessários para montar o livro.
				</p>
			</div>
		);
	}

	return (
		<>
			<section className="mb-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
				<MetricCard
					label="Saldo Itaú 58000"
					value={FinancePresentation.money(itau.bookBalanceCents)}
					detail={balanceControlDetail(itau)}
					tone={itau.balanceDifferenceCents === 0 ? "success" : "attention"}
				/>
				<MetricCard
					label="Transferências bancárias"
					value={FinancePresentation.money(data.summary.bankTransfers.amountCents)}
					detail={`${data.summary.bankTransfers.count} transferências com duas pernas`}
				/>
				<MetricCard
					label="Saldo Caixa Amanda"
					value={FinancePresentation.money(cashAmanda.bookBalanceCents)}
					detail={`${data.summary.cashWithdrawals.count} retiradas recebidas no caixa`}
				/>
				<MetricCard
					label="Saldo Caixa Sindi"
					value={FinancePresentation.money(cashSindi.bookBalanceCents)}
					detail="recebimentos e saídas próprias do caixa"
				/>
				<MetricCard
					label="Saldo consolidado do livro"
					value={FinancePresentation.money(data.summary.bookBalanceCents)}
					detail="soma matemática das sete contas"
					tone={data.summary.bookBalanceCents >= 0 ? "success" : "attention"}
				/>
			</section>

			<section className="mb-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
				{data.accounts.map((account) => (
					<AccountCard
						key={account.id}
						account={account}
						selected={account.id === selectedAccount?.id}
						onSelect={() => setSelectedAccountId(account.id)}
					/>
				))}
			</section>

			{selectedAccount && <AccountLedger account={selectedAccount} />}

			<section className="mt-6 overflow-hidden rounded-3xl border border-stone-200 bg-white shadow-sm">
				<header className="border-b border-stone-200 px-5 py-5">
					<h2 className="font-semibold text-stone-950">Transferências internas</h2>
					<p className="mt-1 text-xs text-stone-500">
						Cada operação cria uma saída na origem e uma entrada igual no destino.
					</p>
				</header>
				<div className="max-h-[520px] divide-y divide-stone-100 overflow-y-auto">
					{data.transfers.map((transfer) => (
						<div
							key={transfer.id}
							className="grid gap-3 px-5 py-4 sm:grid-cols-[90px_minmax(0,1fr)_auto] sm:items-center"
						>
							<p className="text-xs font-medium text-stone-500">
								{FinancePresentation.transactionDate(transfer.occurredOn)}
							</p>
							<div className="flex min-w-0 items-center gap-2 text-sm font-semibold text-stone-800">
								<span className="truncate">{transfer.fromAccountName}</span>
								<ArrowRight className="size-4 shrink-0 text-stone-400" />
								<span className="truncate">{transfer.toAccountName}</span>
								<span className="ml-1 shrink-0 rounded-full bg-stone-100 px-2 py-1 text-[10px] font-semibold text-stone-500">
									{transfer.kind === "CASH_WITHDRAWAL" ? "Espécie" : "Entre bancos"}
								</span>
							</div>
							<p className="text-right text-sm font-semibold tabular-nums text-stone-950">
								{FinancePresentation.money(transfer.amountCents)}
							</p>
						</div>
					))}
				</div>
			</section>
		</>
	);
}

function AccountCard({
	account,
	selected,
	onSelect,
}: {
	account: TreasuryAccount;
	selected: boolean;
	onSelect: () => void;
}) {
	return (
		<button
			type="button"
			onClick={onSelect}
			className={`rounded-2xl border p-5 text-left shadow-sm transition ${selected ? "border-emerald-800 bg-emerald-950 text-white" : "border-stone-200 bg-white hover:border-stone-300"}`}
		>
			<div className="flex items-start justify-between gap-4">
				<span
					className={`grid size-10 place-items-center rounded-xl ${selected ? "bg-white/10" : "bg-stone-100 text-stone-600"}`}
				>
					{account.kind === "CASH" ? (
						<Banknote className="size-5" />
					) : (
						<Landmark className="size-5" />
					)}
				</span>
				<span
					className={`rounded-full px-2.5 py-1 text-[10px] font-semibold ${selected ? "bg-white/10 text-emerald-50" : "bg-stone-100 text-stone-500"}`}
				>
					{account.coverage === "FULL_BANK" ? "Movimentação completa" : "Somente transferências"}
				</span>
			</div>
			<p className={`mt-4 text-sm font-semibold ${selected ? "text-white" : "text-stone-900"}`}>
				{account.name}
				{account.number && <span className="font-normal opacity-60"> · {account.number}</span>}
			</p>
			<p
				className={`mt-1 text-2xl font-semibold tracking-[-0.03em] tabular-nums ${selected ? "text-white" : account.bookBalanceCents < 0 ? "text-rose-700" : "text-stone-950"}`}
			>
				{FinancePresentation.money(account.bookBalanceCents)}
			</p>
			<p className={`mt-2 text-xs ${selected ? "text-emerald-100/70" : "text-stone-500"}`}>
				{account.entries.length} movimentos no livro
			</p>
		</button>
	);
}

function AccountLedger({ account }: { account: TreasuryAccount }) {
	return (
		<section className="overflow-hidden rounded-3xl border border-stone-200 bg-white shadow-sm">
			<header className="flex flex-col justify-between gap-3 border-b border-stone-200 px-5 py-5 sm:flex-row sm:items-end">
				<div>
					<h2 className="font-semibold text-stone-950">Movimentos · {account.name}</h2>
					<p className="mt-1 text-xs text-stone-500">
						Saldo inicial {FinancePresentation.money(account.openingBalanceCents)} · cada linha
						mostra o saldo resultante
					</p>
				</div>
				<p className="text-sm font-semibold tabular-nums text-stone-950">
					Saldo {FinancePresentation.money(account.bookBalanceCents)}
				</p>
			</header>
			<div className="max-h-[620px] overflow-y-auto">
				{account.entries.length === 0 ? (
					<p className="px-6 py-12 text-center text-sm text-stone-500">
						Nenhum movimento registrado nesta conta no mês.
					</p>
				) : (
					account.entries.map((entry) => (
						<div
							key={entry.id}
							className="grid gap-3 border-b border-stone-100 px-5 py-4 last:border-b-0 sm:grid-cols-[42px_82px_minmax(0,1fr)_130px_140px] sm:items-center"
						>
							<span
								className={`grid size-9 place-items-center rounded-xl ${entry.amountCents < 0 ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"}`}
							>
								{entry.amountCents < 0 ? (
									<ArrowUpRight className="size-4" />
								) : (
									<ArrowDownLeft className="size-4" />
								)}
							</span>
							<p className="text-xs font-medium text-stone-500">
								{FinancePresentation.transactionDate(entry.occurredOn)}
							</p>
							<div className="min-w-0">
								<p className="truncate text-sm font-semibold text-stone-800">{entry.description}</p>
								<p className="mt-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-stone-400">
									{entry.source === "TRANSFER_RULE" && <ArrowLeftRight className="size-3" />}
									{entry.source === "TRANSFER_RULE"
										? "Perna da transferência"
										: "Movimento bancário"}
								</p>
							</div>
							<p
								className={`text-right text-sm font-semibold tabular-nums ${entry.amountCents < 0 ? "text-rose-700" : "text-emerald-800"}`}
							>
								{FinancePresentation.signedMoney(entry.amountCents)}
							</p>
							<div className="text-right">
								<p className="text-[10px] font-medium uppercase tracking-wide text-stone-400">
									Saldo
								</p>
								<p className="mt-0.5 text-sm font-semibold tabular-nums text-stone-900">
									{FinancePresentation.money(entry.balanceAfterCents)}
								</p>
							</div>
						</div>
					))
				)}
			</div>
		</section>
	);
}

function balanceControlDetail(account: TreasuryAccount) {
	if (account.observedBalanceCents == null) {
		return "sem saldo externo para conferência";
	}

	if (account.balanceDifferenceCents === 0) {
		return `confere com Pluggy · abertura ${FinancePresentation.money(account.openingBalanceCents)}`;
	}

	if (account.balanceDifferenceCents == null) {
		return `Pluggy ${FinancePresentation.money(account.observedBalanceCents)}`;
	}

	return `Pluggy ${FinancePresentation.money(account.observedBalanceCents)} · diferença ${FinancePresentation.signedMoney(account.balanceDifferenceCents)}`;
}
